import { useEffect, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { AdminOrg, AdminVertical } from '../../api/types';
import { Modal } from '../../components/ui/modal';
import { createOrgBody, defaultVertical } from '../../lib/create-org';

/**
 * The org-type selector on the create form. Its options are **whatever
 * `GET /admin/verticals` returned** — this app carries no list of its own, so a
 * vertical the build does not register cannot be chosen and one it does register
 * cannot be missing because nobody edited this file.
 *
 * When that read failed there is no honest default to fall back on, so it says
 * so plainly and the caller disables the create button: an org's vertical cannot
 * be changed afterwards, so guessing one and creating the org anyway is the one
 * outcome worth preventing. `verticals` empty with no `failed` is the loading
 * moment, and renders nothing.
 *
 * Exported so `new-org-modal.spec.tsx` can render it without a DOM.
 */
export function VerticalField({
  verticals,
  value,
  failed,
  onChange,
}: {
  verticals: readonly AdminVertical[];
  value: string;
  failed: boolean;
  onChange: (key: string) => void;
}) {
  if (failed) {
    return (
      <div className="field">
        <div className="label">Org type</div>
        <div className="field-note">
          The org types could not be loaded, so a new org cannot be created right now. Reload the
          page to try again.
        </div>
      </div>
    );
  }
  if (verticals.length === 0) return null;
  return (
    <div className="field">
      <label className="label" htmlFor="org-vertical">
        Org type
      </label>
      <select id="org-vertical" value={value} onChange={(event) => onChange(event.target.value)}>
        {verticals.map((vertical) => (
          <option key={vertical.key} value={vertical.key}>
            {vertical.label}
          </option>
        ))}
      </select>
      <div className="field-note">Settled when the org is created; it cannot be changed later.</div>
    </div>
  );
}

/**
 * **The create form, in a box over the page** (owner ruling, 2026-09-10: "New
 * organization becomes a button in the top right; the create form moves into a
 * modal").
 *
 * It was the bottom third of the list page, open on every visit, wanted on
 * almost none of them — three fields and a paragraph of field notes under a
 * table an operator came to read. A modal is the honest shape for it: creating a
 * tenant is a moment, not a fixture of the screen.
 *
 * Everything the form needs is held here rather than by the page, so opening the
 * modal is what starts the `GET /admin/verticals` read and closing it forgets a
 * half-typed slug. The failure of *that* read is the one thing this component
 * will not shrug at: an org's vertical cannot be changed after it is written, so
 * a build that could not learn which types exist disables the create rather than
 * guessing one.
 *
 * Exported so the copy sweep can render it open — the state a page driven only
 * by clicks never reaches under `renderToStaticMarkup`.
 */
export function NewOrgModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (org: AdminOrg) => void;
}) {
  const [verticals, setVerticals] = useState<AdminVertical[]>([]);
  const [verticalsFailed, setVerticalsFailed] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The org types on offer, along with the key the server defaults to. */
  useEffect(() => {
    let live = true;
    api
      .listVerticals()
      .then((answer) => {
        if (!live) return;
        setVerticals(answer.verticals);
        setVerticalsFailed(false);
        setVertical((chosen) => (chosen.length > 0 ? chosen : defaultVertical(answer)));
      })
      .catch(() => {
        if (!live) return;
        setVerticals([]);
        setVerticalsFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  const withheld =
    slug.trim().length === 0 || name.trim().length === 0 || creating || verticalsFailed;

  const create = async () => {
    // The button is disabled for all of this, but the guard is here too: creating
    // an org whose vertical was guessed is the one mistake that cannot be undone.
    if (withheld) return;
    setCreating(true);
    try {
      const created = await api.createOrg(createOrgBody({ slug, name, vertical }));
      onCreated(created.org);
    } catch (caught) {
      /* The refusal stays **in the modal**, and so does everything typed: a
         slug already taken is the common one, and the operator's next move is to
         edit the field they are looking at. Closing the box to show them the
         error would take the field away with it. */
      setError(errorLine(caught));
    } finally {
      /* In a `finally` rather than beside the refusal: `creating` is what holds
         the box shut, so a throw out of `onCreated` — the caller's close and
         navigate — would otherwise leave an operator with a modal that no
         longer answers Escape or the backdrop. */
      setCreating(false);
    }
  };

  /* Nothing closes this while a create is in flight — not Escape, not the
     backdrop. The row is being written; a box that vanished mid-write would
     leave an operator with no idea whether it happened. */
  const close = () => {
    if (!creating) onClose();
  };

  return (
    <Modal label="New organization" labelledBy="new-org-title" onClose={close}>
      <div className="modal-head" id="new-org-title">
        New organization
      </div>
      <div className="form">
        <div className="field">
          <label className="label" htmlFor="org-slug">
            Slug
          </label>
          <input
            id="org-slug"
            type="text"
            value={slug}
            disabled={creating}
            onChange={(event) => setSlug(event.target.value)}
          />
          <div className="field-note">
            Lowercase, digits and hyphens. Display-only; the id is what routes use.
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="org-name">
            Name
          </label>
          <input
            id="org-name"
            type="text"
            value={name}
            disabled={creating}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <VerticalField
          verticals={verticals}
          value={vertical}
          failed={verticalsFailed}
          onChange={setVertical}
        />

        {error ? (
          <div className="flag row">
            <div className="label">Error</div>
            <div className="detail">{error}</div>
          </div>
        ) : null}

        <div className="modal-actions">
          <button
            type="button"
            className="btn go"
            disabled={withheld}
            onClick={() => void create()}
          >
            {creating ? 'Creating…' : 'Create organization'}
          </button>
          <button type="button" className="btn act" disabled={creating} onClick={close}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
